// Keep in sync with guestbook/src/themePresets.js so previews resolve the same saved theme.
const INTER = "'Inter', sans-serif";
const LATO = "'Lato', sans-serif";
const POPPINS = "'Poppins', sans-serif";
const PLAYFAIR = "'Playfair Display', serif";

const typography = (heading, body = INTER) => ({
  guide_title_font: heading,
  guide_title_font_size: "24px",
  guide_title_font_weight: "bold",
  section_title_font: heading,
  section_title_font_size: "20px",
  section_title_font_weight: "bold",
  intro_text_font: body,
  intro_text_font_size: "15px",
  intro_text_font_weight: "normal",
  block_title_font: body,
  block_title_font_size: "15px",
  block_title_font_weight: "bold",
  block_text_font: body,
  block_text_font_size: "15px",
  block_text_font_weight: "normal",
});

const colors = ({ background, surface, text, muted, heading, border }) => ({
  page_background_color: background,
  header_background_color: background,
  block_background_color: surface,
  guide_title_color: heading,
  section_title_color: heading,
  intro_text_color: muted,
  block_title_color: heading,
  block_text_color: text,
  border_color: border,
  muted_text_color: muted,
});

export const GUIDE_THEME_PRESETS = [
  {
    id: "modern",
    defaultAccent: "#2563eb",
    defaults: { block_style: "Collapsible", block_border: false, show_icons: true, area_guide_view: "grid" },
    typography: { ...typography(POPPINS), guide_title_font_size: "28px", section_title_font_size: "21px", block_title_font_weight: "normal" },
    colors: { ...colors({ background: "#e9f0ff", surface: "#ffffff", text: "#27364a", muted: "#62738a", heading: "#101c2c", border: "#d3deed" }), block_title_color: "#111111" },
  },
  {
    id: "coastal",
    defaultAccent: "#087f86",
    defaults: { block_style: "Inline", block_border: true, show_icons: true, area_guide_view: "list" },
    typography: { ...typography(LATO, LATO), guide_title_font_size: "28px", section_title_font_size: "22px", block_title_font_size: "16px" },
    colors: colors({ background: "#e4f4ef", surface: "#fff7e8", text: "#315a5b", muted: "#668382", heading: "#0f4e52", border: "#9fc9c5" }),
  },
  {
    id: "boutique",
    defaultAccent: "#8f4058",
    defaults: { block_style: "Inline", block_border: true, show_icons: false, area_guide_view: "grid" },
    typography: { ...typography(PLAYFAIR, LATO), guide_title_font_size: "30px", guide_title_font_weight: "600", section_title_font_size: "23px", section_title_font_weight: "600", block_title_font: PLAYFAIR, block_title_font_size: "17px", block_title_font_weight: "600" },
    colors: colors({ background: "#f8eae4", surface: "#fff8f4", text: "#56413f", muted: "#806965", heading: "#3f272c", border: "#d9beb5" }),
  },
  {
    id: "classic",
    defaultAccent: "#344f68",
    defaults: { block_style: "Collapsible", block_border: true, show_icons: true, area_guide_view: "list" },
    typography: { ...typography(INTER), guide_title_font_size: "24px", section_title_font_size: "19px", block_title_font_size: "15px", block_title_font_weight: "400" },
    colors: colors({ background: "#f5f5f5", surface: "#ffffff", text: "#41464a", muted: "#6e7478", heading: "#252b30", border: "#dedede" }),
  },
];

const getPreset = (themeId) =>
  GUIDE_THEME_PRESETS.find((theme) => theme.id === themeId) || null;

const normalizeHex = (value) => {
  const match = /^#([0-9a-f]{6})$/i.exec(value || "");
  return match ? `#${match[1].toLowerCase()}` : null;
};

const channels = (hex) => {
  const value = normalizeHex(hex);
  if (!value) return null;
  return [1, 3, 5].map((index) => parseInt(value.slice(index, index + 2), 16));
};

const luminance = (hex) => {
  const rgb = channels(hex);
  if (!rgb) return 0;
  const values = rgb.map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  return values[0] * 0.2126 + values[1] * 0.7152 + values[2] * 0.0722;
};

export const contrastRatio = (first, second) => {
  const lighter = Math.max(luminance(first), luminance(second));
  const darker = Math.min(luminance(first), luminance(second));
  return (lighter + 0.05) / (darker + 0.05);
};

const mix = (first, second, amount) => {
  const from = channels(first);
  const to = channels(second);
  if (!from || !to) return first;
  const values = from.map((value, index) =>
    Math.round(value + (to[index] - value) * amount),
  );
  return `#${values.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
};

const safeAccent = (accent, background) => {
  let candidate = normalizeHex(accent);
  if (!candidate) return null;
  const target = luminance(background) < 0.35 ? "#ffffff" : "#000000";
  for (let step = 0; step < 8 && contrastRatio(candidate, background) < 3; step += 1) {
    candidate = mix(candidate, target, 0.14);
  }
  return candidate;
};

const readableText = (background) =>
  contrastRatio(background, "#ffffff") >= 4.5 ? "#ffffff" : "#111827";

export const resolveGuestbookTheme = (guestbook = {}) => {
  const preset = getPreset(guestbook.theme_id);
  if (!preset) return guestbook;

  const accent =
    safeAccent(guestbook.brand_accent || preset.defaultAccent, preset.colors.page_background_color) ||
    preset.defaultAccent;

  return {
    ...guestbook,
    ...preset.defaults,
    ...preset.typography,
    ...preset.colors,
    active_tab_color: accent,
    tab_text_color: readableText(accent),
    distance_color: accent,
    icon_color: accent,
    link_color: accent,
    // Presets provide defaults; explicit Layout choices remain authoritative.
    block_border: guestbook.block_border ?? preset.defaults.block_border,
  };
};
